import { computed, inject, Injectable, signal } from '@angular/core';
import { WorkOrder, WorkOrderFabric } from '../models/order/work-order';
import { DyeingColor } from '../models/production/dyeing/dyeing-color';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { Asset } from '../models/asset/asset';
import _ from 'lodash';
import { ProductionCardsProxyService } from '../proxies/production/production-card.proxy.service';
import { ProductionCard } from '../models/production/production-card';
import { Tags } from '@app/models/tags/tags';
import { DyeingGroup, ProductionCardEntryFormGroup, ProductionCardFormGroup } from '@app/models/production/production-card-forms';

@Injectable({
  providedIn: 'root',
})
export class ProductionCardService {
  private readonly productionCardsProxyService = inject(ProductionCardsProxyService);

  public workOrder!: WorkOrder;
  public colors: DyeingColor[] = [];
  public dyeingAssets: Asset[] = [];
  savedProductionCards: ProductionCard[] = [];
  public productionCardsFormArray: FormArray<FormGroup<ProductionCardFormGroup>> = new FormArray<FormGroup<ProductionCardFormGroup>>([]);

  private _pending = signal(0);
  readonly isLoading = computed(() => this._pending() > 0);

  public initialize(workOrder: WorkOrder, colors: DyeingColor[], dyeingAssets: Asset[], forToSelect = false) {
    this.workOrder = workOrder;
    this.colors = colors;
    this.dyeingAssets = dyeingAssets;

    if (forToSelect) {
      this.loadAllProductionCardsNoRelated();
    } else {
      this.loadAllProductionCards();
    }
  }

  public loadAllProductionCards() {
    this.productionCardsFormArray.clear();
    this._pending.update((v) => v + 1);
    this.productionCardsProxyService.getProductionCardsByWorkOrderIdList(this.workOrder!.id).subscribe({
      next: (response) => {
        this._pending.update((v) => v - 1);
        this.populateFormArray(response.data ?? []);
      },
      error: () => this._pending.update((v) => v - 1),
    });
  }

  public loadAllProductionCardsNoRelated() {
    this.productionCardsFormArray.clear();
    this._pending.update((v) => v + 1);
    this.productionCardsProxyService.getProductionCardsExcludedKnitting(this.workOrder!.id).subscribe({
      next: (response) => {
        this._pending.update((v) => v - 1);
        this.populateFormArray(response.data ?? []);
      },
      error: () => this._pending.update((v) => v - 1),
    });
  }

  public removeProductionCard(id: string) {
    const index = this.productionCardsFormArray.controls.findIndex((fg) => fg.controls.id.value === id);
    if (index !== -1) {
      this.productionCardsFormArray.removeAt(index);
    }
    this.savedProductionCards = this.savedProductionCards.filter((pc) => pc.id !== id);
  }

  public getProductionCardsForDyeingForecast(dyeingForecastId: string) {
    this.productionCardsProxyService.search({ dyeingForecastReference: dyeingForecastId }, 1, 50).subscribe({
      next: (productionCards) => {
        this._pending.update((v) => v - 1);
        const newCards = productionCards.data!.filter((pc) => !this.savedProductionCards.some((saved) => saved.id === pc.id));
        this.savedProductionCards = [...this.savedProductionCards, ...newCards];
        newCards.forEach((pc) => this.productionCardsFormArray.push(this.createProductionCardFormGroup(pc)));
      },
      error: () => this._pending.update((v) => v - 1),
    });
  }

  public get dyeingGroups(): DyeingGroup[] {
    const groupRecord: Record<string, WorkOrderFabric[]> = {};
    this.workOrder.fabrics.forEach((fabric) => {
      fabric.garmetDetails?.forEach((garmetDetail) => {
        (groupRecord[garmetDetail.colorReference] ??= []).push(fabric);
      });
    });

    return Object.entries(groupRecord).reduce<DyeingGroup[]>((acc, [colorReference, fabrics]) => {
      const color = this.colors.find((c) => c.id === colorReference);
      if (color) acc.push({ fabrics, color });
      return acc;
    }, []);
  }

  public getProductionCardEntriesByColor(color: DyeingColor): FormArray<FormGroup<ProductionCardFormGroup>> {
    const filteredControls = this.productionCardsFormArray.controls.filter((fg) => fg.controls.color.value?.id === color.id);
    return new FormArray<FormGroup<ProductionCardFormGroup>>(filteredControls);
  }

  public addNewProductionCard() {
    this.productionCardsFormArray.push(this.createProductionCardFormGroup());
  }

  public allColors(): DyeingColor[] {
    return this.dyeingGroups.map((dg) => dg.color);
  }

  public allAssets(): Asset[] {
    return this.dyeingAssets;
  }

  public updateProductionCardEntries(productionCardFormGroup: FormGroup<ProductionCardFormGroup>, totalQty: number) {
    const entriesFormArray = productionCardFormGroup.controls.productionCardEntries;
    entriesFormArray.clear();

    const dyeingGroup = this.dyeingGroups.find((g) => g.color.id === productionCardFormGroup.controls.color.value?.id);
    if (!dyeingGroup) return;

    dyeingGroup.fabrics.forEach((fabric) => {
      entriesFormArray.push(
        new FormGroup<ProductionCardEntryFormGroup>({
          fabricCode: new FormControl(fabric.fabricCode, { validators: [Validators.required], nonNullable: true }),
          quantity: new FormControl(this.getFabricWeightedPercentage(dyeingGroup, fabric.fabricCode) * totalQty, {
            validators: [Validators.required, Validators.min(0)],
            nonNullable: true,
          }),
        }),
      );
    });

    entriesFormArray.updateValueAndValidity({ emitEvent: false });
  }

  public createProductionCardFormGroup(productionCard?: ProductionCard): FormGroup<ProductionCardFormGroup> {
    const isNew = _.isNil(productionCard);

    let productionCardEntries: FormGroup<ProductionCardEntryFormGroup>[];
    if (productionCard?.productionCardEntries?.length) {
      productionCardEntries = productionCard.productionCardEntries.map(
        (entry) =>
          new FormGroup<ProductionCardEntryFormGroup>({
            fabricCode: new FormControl(entry.fabricCode, { validators: [Validators.required], nonNullable: true }),
            quantity: new FormControl(entry.quantity, { validators: [Validators.required, Validators.min(0)], nonNullable: true }),
          }),
      );
    } else {
      productionCardEntries = (this.workOrder?.fabrics ?? []).map(
        (fabric) =>
          new FormGroup<ProductionCardEntryFormGroup>({
            fabricCode: new FormControl(fabric.fabricCode, { validators: [Validators.required], nonNullable: true }),
            quantity: new FormControl(0, { validators: [Validators.required, Validators.min(0)], nonNullable: true }),
          }),
      );
    }

    const color: DyeingColor | null = isNew ? null : (this.colors.find((c) => c.id === productionCard.colorReference) ?? null);
    const dyeingAsset: Asset | null = isNew ? null : (this.dyeingAssets.find((a) => a.assetId === productionCard.dyeingAssetId) ?? null);

    if (_.isNil(color) && !isNew) {
      console.warn(
        `ProductionCardService: color not found for colorReference="${productionCard.colorReference}" (card id="${productionCard.id}")`,
      );
    }
    if (_.isNil(dyeingAsset) && !isNew) {
      console.warn(
        `ProductionCardService: dyeing asset not found for dyeingAssetId="${productionCard.dyeingAssetId}" (card id="${productionCard.id}")`,
      );
    }

    return new FormGroup<ProductionCardFormGroup>({
      id: new FormControl<string | null>(productionCard?.id ?? null),
      productionCardNumber: new FormControl<string | null>(productionCard?.productionCardNumber ?? null),
      color: new FormControl<DyeingColor | null>(color, { validators: [Validators.required] }),
      colorName: new FormControl<string | null>(color?.name ?? productionCard?.colorName ?? null),
      dyeingAsset: new FormControl<Asset | null>(dyeingAsset, { validators: [Validators.required] }),
      status: new FormControl<number>(productionCard?.status ?? 0, { validators: [Validators.required], nonNullable: true }),
      productionCardEntries: new FormArray<FormGroup<ProductionCardEntryFormGroup>>(productionCardEntries),
      tags: new FormControl<Tags[]>(productionCard?.tags ?? [], { nonNullable: true }),
      isNew: new FormControl<boolean>(isNew, { nonNullable: true }),
      batchingEstimatedDate: new FormControl<Date | null>(productionCard?.batchingEstimatedDate ?? null),
      dyeingEstimatedStartDate: new FormControl<Date | null>(productionCard?.dyeingEstimatedStartDate ?? null),
      finishingEstimatedEndDate: new FormControl<Date | null>(productionCard?.finishingEstimatedEndDate ?? null),
      rollNumbers: new FormControl<string[]>(productionCard?.rollNumbers ?? [], { nonNullable: true }),
    });
  }

  public getAllFabricsCodes(): string[] {
    return [
      ...new Set(
        this.productionCardsFormArray.controls
          .flatMap((c) => c.get('productionCardEntries')?.value ?? [])
          .map((e: { fabricCode: string | null }) => e.fabricCode)
          .filter((code): code is string => !!code && code.trim() !== ''),
      ),
    ];
  }

  private populateFormArray(productionCards: ProductionCard[]) {
    this.savedProductionCards = productionCards;
    productionCards.forEach((pc) => this.productionCardsFormArray.push(this.createProductionCardFormGroup(pc)));
  }

  private getFabricWeightedPercentage(dyeingGroup: DyeingGroup, fabricCode: string): number {
    const total = dyeingGroup.fabrics.reduce((acc, f) => acc + f.quantityInUOM, 0);
    const fabric = dyeingGroup.fabrics.find((f) => f.fabricCode === fabricCode);
    return fabric ? fabric.quantityInUOM / total : 0;
  }
}
